import { KdsTagGroup, KdsText } from "@kazaar-platform/design-system";
import {
  RiFileTextLine,
  RiFlashlightLine,
  RiInboxLine,
  RiQuestionnaireLine,
  RiShoppingBagLine,
  RiStore2Line,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import { Outlet } from "react-router-dom";

import { AuthenticateGuard } from "@/components/AuthenticateGuard/AuthenticateGuard.tsx";
import { Layout, type SideBarItem } from "@/components/Layouts";
import { type NotificationReadDto } from "@/config/apiClient/api.ts";
import { FeatureEnum } from "@/config/apiClient.types";
import { KAZAAR_SUPPORT_URL } from "@/constants/kazaarSupportUrl.constant.ts";
import { useGetCurrentTasksByPhase } from "@/hooks/queries/useDashboard";
import { useNotifications } from "@/hooks/queries/useNotifications.ts";
import { useGetIsFeatureActive } from "@/hooks/useGetIsFeatureActive";

export const ClientLayout = () => {
  const { isActive: isOpenBriefEnabled } = useGetIsFeatureActive(
    FeatureEnum.OPEN_BRIEF
  );
  const { t } = useTranslate();

  const { data: currentTasksData } = useGetCurrentTasksByPhase({});

  const totalTasksCount = currentTasksData?.data?.total || 0;

  const { useGetLatestNotificationsByGroups } = useNotifications();
  const { data: notificationsData } = useGetLatestNotificationsByGroups({
    page: 1,
    size: 10,
    receivedAt: undefined,
  });

  // Display all new notifications (not sent by the current user)
  const newNotifications: NotificationReadDto[] | undefined = useMemo(
    () =>
      notificationsData?.data?.data?.filter((notification) => {
        const authorParticipant = notification?.content?.participants?.find(
          (participant) => participant.isAuthor
        );
        return (
          authorParticipant?.id !== notification.recipient &&
          notification.receivedAt === null
        );
      }),
    [notificationsData]
  );

  const sideBarItems: SideBarItem[] = [
    {
      name: <KdsText size="subtitle-sm">{t("navigation.worklist")}</KdsText>,
      icon: <RiFlashlightLine className="size-sp-md" />,
      href: "/dashboard",
      notificationCount: totalTasksCount,
    },
    {
      name: (
        <div className="flex items-center gap-sp-xs">
          <KdsText size="subtitle-sm">{t("navigation.inbox")}</KdsText>
        </div>
      ),
      icon: <RiInboxLine className="size-sp-md" />,
      href: "/inbox",
      notificationCount: newNotifications?.length,
    },
    {
      name: (
        <KdsText size="subtitle-sm">{t("navigation.order.catalog")}</KdsText>
      ),
      icon: <RiStore2Line className="size-sp-md" />,
      href: "/catalog",
    },
    {
      name: (
        <KdsText size="subtitle-sm">{t("navigation.order.myOrders")}</KdsText>
      ),
      icon: <RiShoppingBagLine className="size-sp-md" />,
      href: "/orders",
    },
    {
      name: <KdsText size="subtitle-sm">{t("navigation.support")}</KdsText>,
      icon: <RiQuestionnaireLine className="size-sp-md" />,
      href: KAZAAR_SUPPORT_URL,
      isBottomItem: true,
      isOpeningInNewTab: true,
    },
  ];

  if (isOpenBriefEnabled) {
    sideBarItems.push({
      name: (
        <div className="flex items-center gap-sp-xs">
          <KdsText size="subtitle-sm">{t("navigation.openBrief")}</KdsText>
          <KdsTagGroup
            tagsProps={[
              {
                variant: "alpha",
                size: "sm",
                children: t("beta", "Beta"),
              },
            ]}
          />
        </div>
      ),
      icon: <RiFileTextLine className="size-sp-md" />,
      href: "/open-brief",
    });
  }

  return (
    <AuthenticateGuard>
      <Layout sideBarItems={sideBarItems}>
        <Outlet />
      </Layout>
    </AuthenticateGuard>
  );
};
